import React, { useState, useEffect, createContext, useContext, useMemo, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MONO = "'DM Mono', monospace";

// ─── Auth Context ─────────────────────────────────────────────────────────────
interface User { id: number; email: string; full_name: string; role: string; company_id: number; agent_code: string; }
interface AuthCtx { user: User | null; token: string | null; login: (e: string, p: string) => Promise<void>; logout: () => void; }
const AuthContext = createContext<AuthCtx>({} as AuthCtx);
const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => { try { return JSON.parse(localStorage.getItem('si_agent_user') || 'null'); } catch { return null; } });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('si_agent_token'));
  const login = async (email: string, password: string) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setToken(res.data.access_token); setUser(res.data.user);
    localStorage.setItem('si_agent_token', res.data.access_token);
    localStorage.setItem('si_agent_user', JSON.stringify(res.data.user));
  };
  const logout = () => {
    setToken(null); setUser(null);
    localStorage.removeItem('si_agent_token'); localStorage.removeItem('si_agent_user');
  };
  return <AuthContext.Provider value={{ user, token, login, logout }}>{children}</AuthContext.Provider>;
}

function useApi() {
  const { token } = useAuth();
  return useMemo(() => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }), [token]);
}

function usePasswordChange() {
  const api = useApi();
  return async () => {
    const current = window.prompt('Parola curentă');
    if (!current) return;
    const next = window.prompt('Parola nouă (minim 6 caractere)');
    if (!next) return;
    const confirm = window.prompt('Confirmă parola nouă');
    if (next !== confirm) {
      window.alert('Parolele nu coincid.');
      return;
    }
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      window.alert('Parola a fost schimbată.');
    } catch (err: any) {
      window.alert(err.response?.data?.detail || 'Nu am putut schimba parola.');
    }
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────
const PRIORITY_META: Record<string, { label: string; color: string; bg: string }> = {
  P1: { label: 'Core strategic', color: '#3C3489', bg: '#12103a' },
  P2: { label: 'Growth',         color: '#534AB7', bg: '#151233' },
  P3: { label: 'Upsell',         color: '#7F77DD', bg: '#16143a' },
  P4: { label: 'Development',    color: '#AFA9EC', bg: '#141330' },
  P5: { label: 'Activation',     color: '#065a60', bg: '#051e20' },
  P6: { label: 'Retention',      color: '#1a936f', bg: '#071e18' },
  P7: { label: 'Niche push',     color: '#c9a227', bg: '#241c07' },
  P8: { label: 'Long tail',      color: '#e63946', bg: '#240c0e' },
  P9: { label: 'Interpretabil',  color: '#f4a261', bg: '#231508' },
};
const PRIORITY_ORDER = ['P1','P2','P3','P4','P5','P6','P7','P9','P8'];

const RV_COLOR: Record<number, string> = { 1: '#3C3489', 2: '#6366f1', 3: '#10b981', 4: '#f59e0b', 5: '#ef4444' };

const S = {
  card: { background: '#111118', border: '1px solid #1a1a25', borderRadius: 12, padding: '20px 24px' } as React.CSSProperties,
  input: { width: '100%', background: '#0a0a0f', border: '1px solid #2a2a35', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'inherit' },
  btn: (color = '#6366f1') => ({ background: color, border: 'none', borderRadius: 8, padding: '9px 18px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } as React.CSSProperties),
  btnGhost: { background: 'none', border: '1px solid #222', borderRadius: 8, padding: '7px 14px', color: '#888', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' } as React.CSSProperties,
};

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState(''); const [password, setPassword] = useState('');
  const [error, setError] = useState(''); const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await login(email, password); }
    catch (err: any) { setError(err.response?.data?.detail || err.message || 'Eroare la autentificare'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <div style={{ width: 360 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg, #3C3489, #6366f1)', borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          </div>
          <div style={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>Sales Intelligence</div>
          <div style={{ color: '#555', fontSize: 12, marginTop: 4 }}>Dashboard Agent · TPC Concept</div>
        </div>
        <form onSubmit={handleSubmit} style={{ ...S.card, padding: 28 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', color: '#666', fontSize: 11, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>Email</label>
            <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="agent@tpcconcept.ro" required />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', color: '#666', fontSize: 11, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>Parolă</label>
            <input style={S.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
          </div>
          {error && <div style={{ background: '#1f0a0a', border: '1px solid #4a1a1a', borderRadius: 8, padding: '9px 12px', color: '#f87171', fontSize: 12, marginBottom: 14 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ ...S.btn('linear-gradient(135deg, #3C3489, #6366f1)' as any), width: '100%', padding: '10px 0' }}>
            {loading ? 'Se autentifică...' : 'Intră în Dashboard'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmtRON = (v: number | null | undefined) =>
  v ? new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(v) + ' RON' : '—';

const fmtK = (v: number | null | undefined): string => {
  if (!v) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M RON`;
  if (v >= 1_000) return `${Math.round(v / 1_000)}k RON`;
  return `${Math.round(v)} RON`;
};

// ─── STATS HEADER ─────────────────────────────────────────────────────────────
function StatsHeader() {
  const api = useApi();
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    api.get('/agent/stats').then(r => setStats(r.data)).catch(() => {});
  }, [api]);

  if (!stats) return null;

  return (
    <div style={{ marginBottom: 20, display: 'grid', gap: 10 }}>
      {/* Sumar 3 cifre */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {[
          { label: 'Clienți', value: stats.total_clients?.toLocaleString('ro-RO') || '—' },
          { label: 'Vânzări totale', value: fmtRON(stats.total_sales_value) },
          { label: 'Oportunități', value: stats.total_potential_articles?.toLocaleString('ro-RO') || '—' },
        ].map(s => (
          <div key={s.label} style={{ background: '#111118', border: '1px solid #1a1a25', borderRadius: 10, padding: '13px 16px' }}>
            <div style={{ color: '#444', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: 5 }}>{s.label}</div>
            <div style={{ color: '#e8e8f0', fontSize: 18, fontWeight: 700, fontFamily: MONO }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Grid P1-P9 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 6 }}>
        {['P1','P2','P3','P4','P5','P6','P7','P8','P9'].map(p => {
          const pc = PRIORITY_META[p];
          const d = stats.potential?.[p];
          return (
            <div key={p} style={{ background: pc.bg, border: `1px solid ${pc.color}44`, borderRadius: 8, padding: '10px 8px', textAlign: 'center' as const }}>
              <div style={{ color: pc.color, fontSize: 11, fontWeight: 700, fontFamily: MONO, marginBottom: 4 }}>{p}</div>
              <div style={{ color: '#e8e8f0', fontSize: 15, fontWeight: 700, fontFamily: MONO }}>{d?.count ?? '—'}</div>
              <div style={{ color: pc.color, fontSize: 10, fontFamily: MONO, marginTop: 3, opacity: 0.85 }}>
                {d?.estimated_value ? fmtK(d.estimated_value) : '—'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── CLUSTER CARD ─────────────────────────────────────────────────────────────
function ClusterCard({ cluster, expanded, onToggle, onSelect }: {
  cluster: any; expanded: boolean; onToggle: () => void; onSelect: (c: any) => void;
}) {
  return (
    <div style={{ border: '1px solid #1a1a25', borderRadius: 12, overflow: 'hidden', background: '#111118' }}>
      <div
        onClick={onToggle}
        style={{ padding: '14px 18px', cursor: 'pointer', userSelect: 'none' as const }}
        onMouseEnter={e => (e.currentTarget.style.background = '#141420')}
        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
              <div style={{ color: '#e8e8f0', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {cluster.name}
              </div>
              <span style={{ background: '#1a1a2e', color: '#6366f1', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 5, fontFamily: MONO, flexShrink: 0 }}>
                {cluster.total_clients} clienți
              </span>
            </div>
            <div style={{ display: 'flex', gap: 14 }}>
              {[1, 2, 3, 4, 5].map(rv => {
                const count = cluster.rv_distribution?.[String(rv)] || 0;
                if (!count) return null;
                const color = RV_COLOR[rv];
                return (
                  <span key={rv} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color, fontSize: 11, fontWeight: 700, fontFamily: MONO }}>RV{rv}</span>
                    <span style={{ color: '#555', fontSize: 11, fontFamily: MONO }}>{count}</span>
                  </span>
                );
              })}
            </div>
          </div>
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#444" strokeWidth="2"
            style={{ flexShrink: 0, marginLeft: 14, transition: 'transform 0.18s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #141420' }}>
          {cluster.clients.map((c: any, i: number) => {
            const rvColor = RV_COLOR[c.rv] || '#666';
            return (
              <div
                key={c.client_id}
                onClick={() => onSelect(c)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 18px', cursor: 'pointer',
                  borderBottom: i < cluster.clients.length - 1 ? '1px solid #0e0e16' : 'none',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#141420')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#d8d8e8', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.client_name || c.client_id}
                  </div>
                  <div style={{ color: '#333', fontSize: 11, fontFamily: MONO, marginTop: 1 }}>{c.client_id}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  {c.p1_opportunities > 0 && (
                    <span style={{ color: '#a78bfa', fontSize: 11, fontFamily: MONO }}>{c.p1_opportunities} P1</span>
                  )}
                  <span style={{ color: '#4ade80', fontSize: 12, fontFamily: MONO }}>
                    {c.total_sales ? new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(c.total_sales) + ' RON' : '—'}
                  </span>
                  <span style={{
                    background: rvColor + '2a', color: rvColor,
                    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 5, fontFamily: MONO,
                  }}>RV{c.rv}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── CLIENTS PAGE ─────────────────────────────────────────────────────────────
function ClientsPage({ onSelect }: { onSelect: (client: any) => void }) {
  const api = useApi();
  const [clusters, setClusters] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const loadClusters = useCallback(() => {
    setLoading(true);
    api.get('/agent/clients-by-cluster')
      .then(r => { setClusters(r.data.clusters || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [api]);

  useEffect(() => { loadClusters(); }, [loadClusters]);

  useEffect(() => {
    if (!search.trim()) { setSearchResults(null); return; }
    const t = setTimeout(() => {
      api.get(`/agent/clients/search?q=${encodeURIComponent(search)}`)
        .then(r => setSearchResults(r.data.clients || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [search, api]);

  const toggleCluster = (name: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '28px 24px' }}>
      <StatsHeader />

      <div style={{ marginBottom: 20 }}>
        <input
          style={S.input}
          placeholder="Caută client după nume sau cod în toți clienții..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {searchResults !== null ? (
        <div style={{ display: 'grid', gap: 6 }}>
          {searchResults.length === 0 ? (
            <div style={{ color: '#444', textAlign: 'center', padding: 40 }}>Niciun client găsit.</div>
          ) : searchResults.map(c => (
            <div
              key={c.client_id}
              onClick={() => onSelect(c)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', background: '#111118', borderRadius: 10,
                border: '1px solid #1a1a25', cursor: 'pointer',
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = '#3C3489')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = '#1a1a25')}
            >
              <div>
                <div style={{ color: '#e0e0e0', fontSize: 14, fontWeight: 500 }}>{c.client_name || c.client_id}</div>
                <div style={{ color: '#444', fontSize: 11, fontFamily: MONO, marginTop: 2 }}>
                  {c.client_id} · {c.client_category}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: '#4ade80', fontSize: 12, fontFamily: MONO }}>
                  {c.total_sales ? new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(c.total_sales) + ' RON' : '—'}
                </span>
                <span style={{
                  background: (RV_COLOR[c.rv] || '#666') + '2a', color: RV_COLOR[c.rv] || '#666',
                  fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 5, fontFamily: MONO,
                }}>RV{c.rv}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#444" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </div>
            </div>
          ))}
        </div>
      ) : loading ? (
        <div style={{ color: '#444', textAlign: 'center', padding: 60 }}>Se încarcă clienții...</div>
      ) : clusters.length === 0 ? (
        <div style={{ color: '#444', textAlign: 'center', padding: 60 }}>Nu există date calculate încă.</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {clusters.map(cluster => (
            <ClusterCard
              key={cluster.name}
              cluster={cluster}
              expanded={expanded.has(cluster.name)}
              onToggle={() => toggleCluster(cluster.name)}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── RECOMMENDATIONS PAGE ─────────────────────────────────────────────────────
function RecommendationsPage({ client, onBack }: { client: any; onBack: () => void }) {
  const api = useApi();
  const [recs, setRecs] = useState<any[]>([]);
  const [clientInfo, setClientInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [excludeBought, setExcludeBought] = useState(true);
  const [priorityFilter, setPriorityFilter] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/recommendations/${client.client_id}?exclude_bought=${excludeBought}&max_results=500`)
      .then(r => { setRecs(r.data.recommendations || []); setClientInfo(r.data.client || null); setLoading(false); })
      .catch(() => setLoading(false));
  }, [api, client.client_id, excludeBought]);

  const availablePriorities = useMemo(
    () => Array.from(new Set(recs.map(r => r.priority))).sort((a, b) => PRIORITY_ORDER.indexOf(a) - PRIORITY_ORDER.indexOf(b)),
    [recs]
  );

  const grouped = useMemo(() => {
    const filtered = priorityFilter ? recs.filter(r => r.priority === priorityFilter) : recs;
    const map: Record<string, any[]> = {};
    for (const r of filtered) {
      (map[r.priority] = map[r.priority] || []).push(r);
    }
    return PRIORITY_ORDER.map(p => ({ priority: p, items: map[p] || [] })).filter(g => g.items.length > 0);
  }, [recs, priorityFilter]);

  const info = clientInfo || client;
  const rvColor = RV_COLOR[info.rv] || '#666';

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '0 24px 40px' }}>
      <div style={{ position: 'sticky', top: 0, background: '#0a0a0f', zIndex: 10, padding: '16px 0 12px', borderBottom: '1px solid #141420', marginBottom: 20 }}>
        <button onClick={onBack} style={{ ...S.btnGhost, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Lista clienți
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ color: '#fff', fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>{info.client_name || info.client_id}</div>
            <div style={{ color: '#444', fontSize: 12, fontFamily: MONO, marginTop: 3 }}>{info.client_id}</div>
            <div style={{ color: '#555', fontSize: 12, marginTop: 4 }}>{info.client_category}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{
              background: rvColor + '2a', color: rvColor, border: `1px solid ${rvColor}55`,
              fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 6, fontFamily: MONO,
            }}>
              RV{info.rv}
            </span>
            <span style={{ color: '#4ade80', fontSize: 14, fontFamily: MONO, fontWeight: 500 }}>
              {info.total_sales ? new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(info.total_sales) + ' RON' : '—'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
          <button
            onClick={() => setPriorityFilter(null)}
            style={{ ...S.btnGhost, padding: '5px 12px', fontSize: 12, borderColor: !priorityFilter ? '#6366f1' : '#222', color: !priorityFilter ? '#a78bfa' : '#888' }}
          >
            Toate
          </button>
          {availablePriorities.map(p => {
            const pc = PRIORITY_META[p];
            const active = priorityFilter === p;
            return (
              <button
                key={p}
                onClick={() => setPriorityFilter(active ? null : p)}
                style={{
                  background: active ? pc.color + '33' : 'none',
                  border: `1px solid ${active ? pc.color : '#222'}`,
                  borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600,
                  color: active ? pc.color : '#666', cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {p}
              </button>
            );
          })}
          <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', color: '#666', fontSize: 13, userSelect: 'none' as const }}>
            <input
              type="checkbox"
              checked={!excludeBought}
              onChange={e => setExcludeBought(!e.target.checked)}
              style={{ accentColor: '#6366f1' }}
            />
            Arată și cumpărate
          </label>
        </div>
      </div>

      {loading ? (
        <div style={{ color: '#444', textAlign: 'center', padding: 60 }}>Se încarcă recomandările...</div>
      ) : grouped.length === 0 ? (
        <div style={{ color: '#444', textAlign: 'center', padding: 60 }}>Nicio recomandare disponibilă.</div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {grouped.map(({ priority, items }) => {
            const pc = PRIORITY_META[priority] || { label: priority, color: '#666', bg: '#111' };
            return (
              <div key={priority} style={{ border: `1px solid ${pc.color}44`, borderRadius: 12, overflow: 'hidden' }}>
                <div style={{
                  background: pc.bg, borderBottom: `1px solid ${pc.color}33`,
                  padding: '11px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ background: pc.color, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, fontFamily: MONO }}>
                      {priority}
                    </span>
                    <span style={{ color: pc.color, fontSize: 13, fontWeight: 600 }}>{pc.label}</span>
                  </div>
                  <span style={{ color: '#444', fontSize: 12 }}>{items.length} articole</span>
                </div>

                {items.map((item: any, i: number) => (
                  <div
                    key={item.article_id + i}
                    style={{
                      padding: '10px 16px',
                      borderBottom: i < items.length - 1 ? '1px solid #131318' : 'none',
                      display: 'flex', alignItems: 'center', gap: 12,
                      background: item.already_bought ? '#0a1a0a' : 'transparent',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: item.already_bought ? '#6a9a6a' : '#d0d0d0', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.article_name || item.article_id}
                      </div>
                      {item.article_name && item.article_name !== item.article_id && (
                        <div style={{ color: '#444', fontSize: 11, fontFamily: MONO, marginTop: 1 }}>{item.article_id}</div>
                      )}
                    </div>

                    <span style={{
                      background: pc.color + '22', color: pc.color,
                      fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                      fontFamily: MONO, flexShrink: 0,
                    }}>
                      GP{item.gp}
                    </span>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <div style={{ width: 64, height: 4, background: '#1a1a25', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 2, background: pc.color,
                          width: `${Math.min(100, item.penetration_display || 0)}%`,
                        }} />
                      </div>
                      <span style={{ color: '#888', fontSize: 12, fontFamily: MONO, minWidth: 38, textAlign: 'right' }}>
                        {item.penetration_display != null ? `${item.penetration_display}%` : '—'}
                      </span>
                      <span style={{ color: '#3a3a45', fontSize: 11 }}>din segment</span>
                    </div>

                    {item.already_bought && (
                      <span style={{ color: '#4ade80', fontSize: 10, fontWeight: 600, background: '#0a1f0a', border: '1px solid #1a4a1a', padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>
                        ✓ cumpărat
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
function Dashboard() {
  const { user, logout } = useAuth();
  const changePassword = usePasswordChange();
  const [page, setPage] = useState<'clients' | 'recommendations'>('clients');
  const [selectedClient, setSelectedClient] = useState<any>(null);

  const handleSelect = (client: any) => {
    setSelectedClient(client);
    setPage('recommendations');
  };

  const handleBack = () => {
    setPage('clients');
    setSelectedClient(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', fontFamily: "'DM Sans', sans-serif", display: 'flex', flexDirection: 'column' }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />

      <div style={{ borderBottom: '1px solid #141420', padding: '0 24px', display: 'flex', alignItems: 'center', height: 50, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <div style={{ width: 24, height: 24, background: 'linear-gradient(135deg, #3C3489, #6366f1)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          </div>
          <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>Sales Intelligence</span>
          <span style={{ color: '#333', fontSize: 14 }}>·</span>
          <span style={{ color: '#555', fontSize: 13 }}>
            {page === 'recommendations' && selectedClient
              ? selectedClient.client_name || selectedClient.client_id
              : 'Clienți'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#3a3a45', fontSize: 12 }}>{user?.full_name || user?.email}</span>
          <button onClick={changePassword} style={S.btnGhost}>Parolă</button>
          <button onClick={logout} style={S.btnGhost}>Ieși</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {page === 'clients' && <ClientsPage onSelect={handleSelect} />}
        {page === 'recommendations' && selectedClient && (
          <RecommendationsPage client={selectedClient} onBack={handleBack} />
        )}
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
function AppInner() {
  const { user } = useAuth();
  return user ? <Dashboard /> : <LoginPage />;
}
